import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import TopBar from '@/components/TopBar';
import { Input, Label, Select, Card, CardSm, Pill } from '@/components/ui/Form';
import { Button, IconButton } from '@/components/ui/Button';
import { fileToBase64 } from '@/lib/utils';
import { MapPin, QrCode, Camera, Sparkle, Crosshair, CheckCircle, Copy } from '@phosphor-icons/react';
import QRScanner from '@/components/QRScanner';
import { ASIAN_MANGROVES, SPECIES_BY_NAME, REGIONS, speciesForRegion } from '@/lib/mangroveSpecies';

const STATUSES = [
  { v: 'alive', label: 'Alive' },
  { v: 'new_planting', label: 'New planting' },
  { v: 'regenerated', label: 'Regenerated' },
  { v: 'dead', label: 'Dead' },
  { v: 'missing', label: 'Missing' },
];

/** Fuzzy-match an AI-suggested scientific name to the provided list. */
function matchSpecies(suggested, list) {
  if (!suggested) return null;
  const items = list || ASIAN_MANGROVES;
  const norm = suggested.toLowerCase().trim();
  // Direct hit
  for (const m of items) {
    if (m.scientific.toLowerCase() === norm) return m.scientific;
  }
  // Genus + species token match (first two words)
  const tokens = norm.split(/\s+/).slice(0, 2).join(' ');
  for (const m of items) {
    if (m.scientific.toLowerCase().includes(tokens)) return m.scientific;
  }
  // Genus-only fallback
  const genus = norm.split(/\s+/)[0];
  for (const m of items) {
    if (m.scientific.toLowerCase().startsWith(genus + ' ')) return m.scientific;
  }
  return null;
}

export default function TreeRegistration() {
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [plots, setPlots] = useState([]);
  const [projects, setProjects] = useState([]);
  const [plotId, setPlotId] = useState(searchParams.get('plot') || '');
  const [code, setCode] = useState('');
  const [qrTag, setQrTag] = useState('');
  const [gps, setGps] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsErr, setGpsErr] = useState('');
  const [species, setSpecies] = useState('');
  const [scientific, setScientific] = useState('');
  const [family, setFamily] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiImage, setAiImage] = useState(null);
  const [status, setStatus] = useState('alive');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [qrOpen, setQrOpen] = useState(false);

  const [plotCommonSpecies, setPlotCommonSpecies] = useState([]);
  const [lastTree, setLastTree] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/plots'), api.get('/projects')]).then(([pr, pj]) => {
      setPlots(pr.data);
      setProjects(pj.data);
      if (!plotId && pr.data.length > 0) setPlotId(pr.data[0].id);
    });
  }, []);

  // Compute top-5 species already registered in the selected plot
  // AND remember the single most-recent tree so we can offer one-tap "copy".
  useEffect(() => {
    if (!plotId) { setPlotCommonSpecies([]); setLastTree(null); return; }
    api.get('/trees', { params: { plot_id: plotId } }).then(({ data }) => {
      const counts = {};
      for (const t of data) {
        const name = t.species_scientific;
        if (!name) continue;
        counts[name] = (counts[name] || 0) + 1;
      }
      const top = Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, n]) => ({ name, n }));
      setPlotCommonSpecies(top);
      const sorted = [...data].sort((a, b) =>
        (b.registration_date || '').localeCompare(a.registration_date || '')
      );
      setLastTree(sorted[0] || null);
    }).catch(() => { setPlotCommonSpecies([]); setLastTree(null); });
  }, [plotId]);

  const copyFromLast = () => {
    if (!lastTree) return;
    applySpecies(
      lastTree.species_scientific || '',
      lastTree.species_common,
      lastTree.taxonomy_family
    );
    if (lastTree.tree_status) setStatus(lastTree.tree_status);
  };

  // Resolve the active region from the selected plot -> project.
  const activePlot = plots.find((p) => p.id === plotId);
  const activeProject = activePlot
    ? projects.find((p) => p.id === activePlot.project_id)
    : null;
  const activeRegion = activeProject?.species_region || 'southeast_asia';
  const speciesList = speciesForRegion(activeRegion);
  const speciesInList = !scientific
    || speciesList.some((s) => s.scientific === scientific);

  const captureGPS = () => {
    setGpsErr(''); setGpsBusy(true);
    if (!navigator.geolocation) {
      setGpsErr('Geolocation unavailable'); setGpsBusy(false); return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          altitude: pos.coords.altitude,
        });
        setGpsBusy(false);
      },
      (err) => { setGpsErr(err.message); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const onAiImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAiBusy(true); setErr('');
    try {
      const b64 = await fileToBase64(f);
      setAiImage(b64);
      const { data } = await api.post('/ai/species', {
        image_base64: b64, mime_type: f.type || 'image/jpeg',
      });
      setAiResult(data.result);
      if (data.result?.species_scientific) {
        // Snap AI-suggested scientific name to the closest dropdown entry (so
        // the Select shows the value and common/family auto-fill).
        const matched = matchSpecies(data.result.species_scientific, speciesList);
        applySpecies(matched || data.result.species_scientific,
                     data.result.species_common, data.result.taxonomy_family);
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'AI species ID failed');
    } finally { setAiBusy(false); }
  };

  const applySpecies = (sciName, fallbackCommon, fallbackFamily) => {
    setScientific(sciName);
    const entry = SPECIES_BY_NAME[sciName];
    setSpecies(entry?.common || fallbackCommon || '');
    setFamily(entry?.family || fallbackFamily || '');
  };

  const onScientificChange = (e) => {
    applySpecies(e.target.value);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!gps) { setErr('Capture GPS first'); return; }
    if (!plotId) { setErr('Choose a plot'); return; }
    setSaving(true); setErr('');
    try {
      const tree = await api.post('/trees', {
        plot_id: plotId,
        tree_code: code || undefined,
        qr_tag_id: qrTag || undefined,
        species_scientific: scientific || undefined,
        species_common: species || undefined,
        taxonomy_family: family || undefined,
        gps_lat: gps.lat,
        gps_lon: gps.lon,
        gps_altitude: gps.altitude || null,
        gps_accuracy_m: gps.accuracy,
        tree_status: status,
      });
      nav(`/trees/${tree.data.id}/measure`);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const gpsOK = gps && gps.accuracy <= 5;

  return (
    <div data-testid="tree-registration">
      <QRScanner
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        onDetect={(text) => { setQrTag(text); setQrOpen(false); }}
      />
      <TopBar title="Register Tree" back />
      <form onSubmit={save} className="p-4 space-y-4">
        {/* Plot */}
        <div>
          <Label>Sample plot (PSP)</Label>
          {plots.length === 0 ? (
            <CardSm><p className="text-sm">Create a project & plot first.</p></CardSm>
          ) : (
            <Select value={plotId} onChange={(e) => setPlotId(e.target.value)} required data-testid="plot-select">
              {plots.map((p) => <option key={p.id} value={p.id}>{p.plot_code}</option>)}
            </Select>
          )}
          {lastTree && (
            <button
              type="button"
              onClick={copyFromLast}
              data-testid="copy-from-last-btn"
              className="mt-2 w-full min-h-[44px] border-2 border-carbon bg-surface-alt hover:bg-white text-carbon inline-flex items-center justify-center gap-2 font-display font-bold uppercase tracking-widest text-xs"
              title={`Copy species + status from ${lastTree.tree_code || 'last tree'}`}
            >
              <Copy size={16} weight="bold" />
              Copy from last tree
              <span className="mono text-[10px] text-carbon-soft normal-case tracking-normal">
                ({lastTree.tree_code || lastTree.id.slice(0, 6)})
              </span>
            </button>
          )}
        </div>

        {/* IDs */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Tree code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="auto" data-testid="tree-code-input" />
          </div>
          <div>
            <Label>QR / NFC tag</Label>
            <div className="flex gap-2">
              <Input value={qrTag} onChange={(e) => setQrTag(e.target.value)} placeholder="scan or type" data-testid="qr-tag-input" />
              <IconButton type="button" aria-label="Scan QR" title="Scan QR / NFC tag" data-testid="qr-scan-btn"
                          onClick={() => setQrOpen(true)}>
                <QrCode size={20} weight="bold" />
              </IconButton>
            </div>
          </div>
        </div>

        {/* GPS */}
        <Card data-testid="gps-card">
          <div className="flex items-center justify-between mb-2">
            <span className="font-display font-bold uppercase tracking-widest text-xs">GPS Position</span>
            {gps && (
              <Pill color={gpsOK ? '#059669' : '#D97706'}>
                ±{gps.accuracy.toFixed(1)} m
              </Pill>
            )}
          </div>
          {gps ? (
            <p className="mono text-sm">
              {gps.lat.toFixed(6)}, {gps.lon.toFixed(6)}
            </p>
          ) : (
            <p className="mono text-sm text-carbon-soft">Press capture to acquire fix.</p>
          )}
          {gpsErr && <p className="text-sm text-status-severe mt-2">{gpsErr}</p>}
          <Button type="button" onClick={captureGPS} variant="dark" className="mt-3 w-full" data-testid="capture-gps-btn" disabled={gpsBusy}>
            <Crosshair size={20} weight="bold" />
            {gpsBusy ? 'Acquiring…' : gps ? 'Re-capture GPS' : 'Capture GPS'}
          </Button>
        </Card>

        {/* AI species */}
        <Card data-testid="species-card">
          <div className="flex items-center justify-between mb-2">
            <span className="font-display font-bold uppercase tracking-widest text-xs">Species</span>
            <Sparkle size={16} weight="bold" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="mb-0">Scientific name</Label>
              {activeProject && (
                <span
                  className="text-[10px] mono uppercase tracking-widest text-carbon-soft"
                  data-testid="region-filter-badge"
                  title={`Filtered by project region: ${REGIONS.find(r => r.code === activeRegion)?.label || activeRegion}`}
                >
                  Region: {activeRegion.replace('_', ' ')}
                </span>
              )}
            </div>

            {/* Quick-pick: species already in this plot (top 5 by frequency) */}
            {plotCommonSpecies.length > 0 && (
              <div className="mb-2" data-testid="quick-pick-row">
                <p className="text-[10px] mono uppercase tracking-widest text-carbon-soft mb-1">
                  Common in this plot · tap to pick
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {plotCommonSpecies.map(({ name, n }) => {
                    const entry = SPECIES_BY_NAME[name];
                    const isActive = scientific === name;
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => applySpecies(name)}
                        data-testid={`quick-pick-${name.replace(/\s+/g, '-')}`}
                        className={`text-[11px] px-2 py-1 border-2 border-carbon font-display font-bold tracking-tight inline-flex items-center gap-1 ${
                          isActive ? 'bg-carbon text-canvas' : 'bg-surface-alt hover:bg-white text-carbon'
                        }`}
                        title={entry?.common ? `${entry.common}${entry.local ? ' / ' + entry.local : ''}` : name}
                      >
                        <span className="italic">{name}</span>
                        <span className={`mono font-bold ${isActive ? 'text-canvas/70' : 'text-carbon-soft'}`}>·{n}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <Select
              value={speciesInList ? scientific : '__off_list__'}
              onChange={onScientificChange}
              data-testid="species-scientific-input"
            >
              <option value="">— Select species —</option>
              {speciesList.map((m) => (
                <option key={m.scientific} value={m.scientific}>
                  {m.scientific}{m.common ? ` — ${m.common}` : ''}
                </option>
              ))}
              {!speciesInList && (
                <option value="__off_list__">{scientific} (off-region)</option>
              )}
            </Select>
            {!speciesInList && (
              <p className="text-[11px] mono text-status-mild mt-1" data-testid="off-region-hint">
                "{scientific}" is not typical for this region — kept anyway.
              </p>
            )}
          </div>
          <div className="mt-3">
            <Label>Common / local name</Label>
            <Input
              value={species}
              onChange={(e) => setSpecies(e.target.value)}
              placeholder="auto-filled — edit if needed"
              data-testid="species-common-input"
            />
            {scientific && SPECIES_BY_NAME[scientific]?.local && (
              <p className="text-[11px] mono text-carbon-soft mt-1">
                Regional: {SPECIES_BY_NAME[scientific].local}
              </p>
            )}
          </div>
          <div className="mt-3">
            <Label>Family</Label>
            <Input
              value={family}
              onChange={(e) => setFamily(e.target.value)}
              placeholder="auto-filled"
              data-testid="species-family-input"
            />
          </div>

          <label className="mt-3 block">
            <span className="sr-only">AI photo</span>
            <input type="file" accept="image/*" onChange={onAiImage} className="hidden" data-testid="ai-species-file" id="ai-species-file" />
            <Button as="span" variant="outline" className="w-full cursor-pointer" data-testid="ai-species-btn">
              <Camera size={20} weight="bold" />
              {aiBusy ? 'Analyzing…' : 'Identify with AI (photo)'}
            </Button>
          </label>

          {aiResult && (
            <CardSm className="mt-3" data-testid="ai-species-result">
              <p className="font-display font-bold text-sm">
                AI suggestion: <span className="italic">{aiResult.species_scientific || '—'}</span>
              </p>
              <p className="text-xs mono">
                confidence {((aiResult.confidence || 0) * 100).toFixed(0)}%
              </p>
              {aiResult.notes && <p className="text-xs mt-1 text-carbon-soft">{aiResult.notes}</p>}
            </CardSm>
          )}
        </Card>

        {/* Status */}
        <div>
          <Label>Status</Label>
          <div className="grid grid-cols-3 gap-2" data-testid="status-pills">
            {STATUSES.map((s) => (
              <button
                key={s.v}
                type="button"
                onClick={() => setStatus(s.v)}
                data-testid={`status-${s.v}`}
                className={`min-h-[44px] border-2 border-carbon text-xs font-display font-bold uppercase tracking-widest ${
                  status === s.v ? 'bg-carbon text-canvas' : 'bg-white text-carbon hover:bg-surface-alt'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="border-2 border-status-severe bg-red-50 px-3 py-2 text-sm text-status-severe">{err}</div>}

        <Button type="submit" disabled={saving} className="w-full" size="lg" data-testid="save-tree-btn">
          {saving ? 'Saving…' : 'Save & continue to measurement'}
          <CheckCircle size={20} weight="bold" />
        </Button>
      </form>
    </div>
  );
}
